import React from 'react';

import { Box, Grid, Typography } from '@mui/material';

import { palette, lightPalette, shape } from '@app/theme';

import type { StrengthsWeaknessesProperties } from './StrengthsWeaknesses.types';


/**
Split "Title: description" into bold title + regular description
*/
const NoteText: React.FC<{ text: string }> = ({ text }) => {
  const colonIndex = text.indexOf(':');
  if (colonIndex === -1 || colonIndex > 60) {
    return <>{text}</>;
  }
  const title = text.slice(0, colonIndex);
  const rest = text.slice(colonIndex + 1);
  return (
    <>
      <Typography component='span' sx={{ fontWeight: 700, color: lightPalette.text.primary }}>
        {title}:
      </Typography>
      {rest}
    </>
  );
};

interface AccentCardProperties {
  item: string;
  accentColor: string;
  backgroundColor: string;
  isLast: boolean;
  dataTest?: string;
}

const AccentCard: React.FC<AccentCardProperties> = ({
  item,
  accentColor,
  backgroundColor,
  isLast,
  dataTest,
}) => (
  <Box
    data-testid={dataTest}
    sx={{
      p: '12px 16px',
      borderRadius: `${shape.borderRadius}px`,
      backgroundColor,
      borderLeft: `3px solid ${accentColor}`,
      mb: isLast ? 0 : 1,
    }}
  >
    <Typography
      sx={{
        fontSize: 13,
        lineHeight: 1.55,
        color: lightPalette.text.secondary,
      }}
    >
      <NoteText text={item} />
    </Typography>
  </Box>
);

export const StrengthsWeaknesses: React.FC<StrengthsWeaknessesProperties> = ({
  strengths,
  risks,
  dataTest = 'strengths-weaknesses',
}) => {
  return (
    <Box data-testid={dataTest}>
      <Grid container spacing={2.5}>
        {/* Strengths column */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            data-testid={`${dataTest}_strengths_title`}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: palette.success.main,
              mb: 1.5,
            }}
          >
            Сильные стороны
          </Typography>

          <Box data-testid={`${dataTest}_strengths_list`}>
            {strengths.map((item, index) => (
              <AccentCard
                key={index}
                item={item}
                accentColor={palette.success.main}
                backgroundColor={palette.success.light}
                isLast={index === strengths.length - 1}
                dataTest={`${dataTest}_strength_${index}`}
              />
            ))}
          </Box>
        </Grid>

        {/* Risks column */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            data-testid={`${dataTest}_risks_title`}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: palette.error.main,
              mb: 1.5,
            }}
          >
            Критические риски
          </Typography>

          <Box data-testid={`${dataTest}_risks_list`}>
            {risks.map((item, index) => (
              <AccentCard
                key={index}
                item={item}
                accentColor={palette.error.main}
                backgroundColor={palette.error.light}
                isLast={index === risks.length - 1}
                dataTest={`${dataTest}_risk_${index}`}
              />
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
